import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import ChartCard from './ChartCard';
import ChartLegend from './ChartLegend';
import { RechartsTooltip } from './ChartTooltip';
import {
  BAR_MAX_SIZE,
  CHART_INK,
  CHART_SURFACE,
  STATUS_COLORS,
} from './chartTheme';
import { useStatusLabels } from './useStatusLabels';
import { FieldStatistics, STATUS_KEYS } from '../../utils/statistics';
import { COMMON_TRANSLATE_KEYS } from '../../constants/translate-keys';

interface FieldsBreakdownProps {
  fields: FieldStatistics[];
}

const ROW_HEIGHT = 40;

const FieldsBreakdown = ({ fields }: FieldsBreakdownProps) => {
  const { t } = useTranslation();

  const labels = useStatusLabels();

  return (
    <ChartCard
      title={t(COMMON_TRANSLATE_KEYS.STATS_BY_FIELD)}
      legend={
        <ChartLegend
          items={STATUS_KEYS.map((key) => ({
            key,
            label: labels[key],
            color: STATUS_COLORS[key],
          }))}
        />
      }
    >
      <div className="w-full" style={{ height: fields.length * ROW_HEIGHT + 40 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={fields}
            layout="vertical"
            margin={{ top: 0, right: 16, left: 0, bottom: 0 }}
          >
            <CartesianGrid horizontal={false} stroke={CHART_INK.grid} />
            <XAxis
              type="number"
              allowDecimals={false}
              tick={{ fill: CHART_INK.muted, fontSize: 12 }}
              axisLine={{ stroke: CHART_INK.axis }}
              tickLine={false}
            />
            <YAxis
              type="category"
              dataKey="name"
              width={110}
              tick={{ fill: CHART_INK.secondary, fontSize: 12 }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              cursor={{ fill: 'rgba(255,255,255,0.05)' }}
              content={
                <RechartsTooltip labels={labels} getTitle={(item) => item.name} />
              }
            />
            {STATUS_KEYS.map((key, index) => (
              <Bar
                key={key}
                dataKey={key}
                stackId="status"
                fill={STATUS_COLORS[key]}
                stroke={CHART_SURFACE}
                strokeWidth={1}
                maxBarSize={BAR_MAX_SIZE}
                radius={index === STATUS_KEYS.length - 1 ? [0, 4, 4, 0] : 0}
                isAnimationActive={false}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead className="text-textGray border-b border-settingsDividerGray">
            <tr>
              <th className="py-2 pr-4 font-normal">
                {t(COMMON_TRANSLATE_KEYS.FIELD)}
              </th>
              <th className="py-2 px-2 font-normal text-center">
                {t(COMMON_TRANSLATE_KEYS.STATS_TOTAL)}
              </th>
              {STATUS_KEYS.map((key) => (
                <th key={key} className="py-2 px-2 font-normal text-center">
                  {labels[key]}
                </th>
              ))}
              <th className="py-2 pl-2 font-normal text-center">
                {t(COMMON_TRANSLATE_KEYS.STATS_PLAYERS_VISITED_FIELD)}
              </th>
            </tr>
          </thead>
          <tbody className="text-mainWhite tabular-nums">
            {fields.map((field) => (
              <tr
                key={field.fieldId}
                className="border-b border-settingsDividerGray last:border-0"
              >
                <td className="py-2 pr-4">{field.name}</td>
                <td className="py-2 px-2 text-center">{field.total}</td>
                {STATUS_KEYS.map((key) => (
                  <td key={key} className="py-2 px-2 text-center">
                    {field[key]}
                  </td>
                ))}
                <td className="py-2 pl-2 text-center">{field.players}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </ChartCard>
  );
};

export default FieldsBreakdown;
